import { signW3C, deriveW3C, verifyW3CSignature } from '../w3c';
import { assertCredentialStatus, assertTransferableRecords } from '@trustvc/w3c-credential-status';
import { verifyCredentialStatus } from '@trustvc/w3c-vc';
import { ethers } from 'ethers';
import { constants } from '@tradetrust-tt/token-registry-v4';
import { constants as constants$1 } from '@tradetrust-tt/token-registry-v5';
import { v4Contracts } from '../token-registry-v4';
import { v5Contracts } from '../token-registry-v5';
import { SUPPORTED_CHAINS } from '../utils';
import { TR_CONTEXT_URL, RENDER_CONTEXT_V2_URL, QRCODE_CONTEXT_URL, DATA_INTEGRITY_V2_URL, VC_V1_URL, VC_V2_URL } from '@trustvc/w3c-context';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
class DocumentBuilder {
  static {
    __name(this, "DocumentBuilder");
  }
  document;
  // Holds the document to be built and signed.
  documentType = "w3c";
  // Default to W3C
  selectedStatusType = null;
  // Tracks selected status type.
  statusConfig = {};
  // Configuration for the credential status.
  rpcProviderUrl;
  // Holds the RPC provider URL for verifying token registry.
  requiredFields = ["credentialSubject"];
  // Required fields that must be present in the document.
  isSigned = false;
  // Tracks if a document is signed
  isDerived = false;
  // Tracks if a document is derived
  /**
   * Constructor to initialize the document builder.
   * @param {Partial<VerifiableCredential>} input - The input document.
   * @param {string} [documentType] - The type of the document (default is "w3c").
   */
  constructor(input, documentType = "w3c") {
    this.document = this.initializeDocument(input);
    this.documentType = documentType;
  }
  // Sets the credential subject of the document.
  credentialSubject(subject) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    this.document.credentialSubject = subject;
    return this;
  }
  // Configures the credential status of the document based on the provided type (Transferable Records or Verifiable Document).
  credentialStatus(config) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    const isTransferable = this.isTransferableRecordsConfig(config);
    const isVerifiable = this.isVerifiableDocumentConfig(config);
    if (isTransferable && isVerifiable) {
      throw new Error(
        "Configuration Error: Do not mix transferable records and verifiable document properties."
      );
    }
    if (isTransferable) {
      this.selectedStatusType = "transferableRecords";
      this.statusConfig = {
        type: "TransferableRecords",
        tokenNetwork: { chain: config.chain, chainId: config.chainId },
        tokenRegistry: config.tokenRegistry
      };
      this.rpcProviderUrl = config.rpcProviderUrl;
      this.addContext(TR_CONTEXT_URL);
    } else if (isVerifiable) {
      this.selectedStatusType = "verifiableDocument";
      this.statusConfig = {
        id: `${config.url}#${config.index}`,
        type: "BitstringStatusListEntry",
        statusPurpose: config.purpose || "revocation",
        // Set status purpose to "revocation" by default.
        statusListIndex: config.index,
        statusListCredential: config.url
      };
    } else {
      throw new Error("Configuration Error: Missing required fields for credential status.");
    }
    return this;
  }
  // Sets the expiration date of the document.
  expirationDate(date) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    this.document.validUntil = typeof date === "string" ? date : date.toISOString();
    return this;
  }
  // Defines the rendering method for the document.
  renderMethod(method) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    this.document.renderMethod = [method];
    this.addContext(RENDER_CONTEXT_V2_URL);
    return this;
  }
  // Defines the qrcode for the document.
  qrCode(method) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    this.document.qrCode = method;
    this.addContext(QRCODE_CONTEXT_URL);
    return this;
  }
  // Sign the document using the provided private key and an optional cryptographic suite.
  async sign(privateKey, cryptoSuite, options) {
    if (this.isSigned) throw new Error("Configuration Error: Document is already signed.");
    if (cryptoSuite === "BbsBlsSignature2020") {
      throw new Error(
        "BbsBlsSignature2020 is no longer supported. Please use the latest cryptosuite versions instead"
      );
    }
    if (this.selectedStatusType) {
      this.document.credentialStatus = this.statusConfig;
    }
    this.validateRequiredFields(this.document);
    if (this.selectedStatusType === "verifiableDocument") {
      assertCredentialStatus(this.document.credentialStatus);
      const verificationResult = await verifyCredentialStatus(this.document.credentialStatus);
      if (verificationResult.error)
        throw new Error(`Credential Verification Failed: ${verificationResult.error}`);
      if (verificationResult.status)
        throw new Error("Credential Verification Failed: Invalid credential status detected.");
    } else if (this.selectedStatusType === "transferableRecords") {
      assertTransferableRecords(this.document.credentialStatus, "sign");
      await this.verifyTokenRegistry();
    }
    this.document.issuer = privateKey.id.split("#")[0];
    this.document.validFrom = this.document.validFrom || (/* @__PURE__ */ new Date()).toISOString();
    this.addContext(DATA_INTEGRITY_V2_URL);
    const signedVC = await signW3C(this.document, privateKey, cryptoSuite, options);
    if (signedVC.error) throw new Error(`Signing Error: ${signedVC.error}`);
    this.isSigned = true;
    return signedVC.signed;
  }
  async derive(revealedAttributes) {
    if (!this.isSigned) throw new Error("Configuration Error: Document is not signed yet.");
    if (this.isDerived) throw new Error("Configuration Error: Document is already derived.");
    const derivedCredential = await deriveW3C(
      this.document,
      revealedAttributes
    );
    if (derivedCredential.error) throw new Error(`Derivation Error: ${derivedCredential.error}`);
    this.document = derivedCredential.derived;
    this.isDerived = true;
    return derivedCredential.derived;
  }
  // Verify the document.
  async verify() {
    if (!this.isSigned) throw new Error("Verification Error: Document is not signed yet.");
    if (!this.isDerived) {
      throw new Error("Verification Error: Document is not derived yet. Use derive() first.");
    }
    const verificationResult = await verifyW3CSignature(
      this.document
    );
    if (verificationResult.error)
      throw new Error(`Verification Error: ${verificationResult.error}`);
    return verificationResult.verified;
  }
  // Returns the current state of the document as a JSON string.
  toString() {
    return JSON.stringify(this.document, null, 2);
  }
  // Type guard for transferable records configuration
  isTransferableRecordsConfig(config) {
    return config && typeof config.tokenRegistry === "string" && typeof config.chain === "string" && typeof config.chainId === "number" && typeof config.rpcProviderUrl === "string";
  }
  // Type guard for verifiable document configuration
  isVerifiableDocumentConfig(config) {
    return config && typeof config.url === "string" && typeof config.index === "number";
  }
  // Private helper method to validate that the required fields are present in the input document.
  validateRequiredFields(input) {
    this.requiredFields.forEach((field) => {
      if (!input[field]) {
        throw new Error(`Validation Error: Missing required field "${field}" in the credential.`);
      }
    });
  }
  // Private helper method to initialize the document with required context and type, adding the necessary context URL.
  initializeDocument(input) {
    if (input.proof) throw new Error("Configuration Error: Document is already signed.");
    return {
      ...input,
      "@context": this.buildContext(input["@context"]),
      type: Array.from(new Set([].concat(input.type || [], "VerifiableCredential")))
    };
  }
  // Private helper method to build the context for the document, ensuring uniqueness and adding the default W3C context.
  buildContext(context) {
    const arrayContext = Array.isArray(context) ? context : context ? [context] : [];
    if (arrayContext.includes(VC_V1_URL)) {
      throw new Error("Document builder does not support data model v1.1.");
    }
    return [VC_V2_URL, ...arrayContext].filter((v, i, a) => a.indexOf(v) === i);
  }
  // Private helper method to add a new context to the document if it does not already exist.
  addContext(context) {
    if (!this.document["@context"].includes(context)) {
      this.document["@context"].push(context);
    }
  }
  // Private helper method to verify that the token registry supports the required interface for transferable records.
  async verifyTokenRegistry() {
    const chainId = this.document.credentialStatus.tokenNetwork.chainId;
    if (!(chainId in SUPPORTED_CHAINS)) {
      throw new Error(`Unsupported Chain: Chain ID ${chainId} is not supported.`);
    }
    try {
      const provider = new ethers.providers.JsonRpcProvider(this.rpcProviderUrl);
      const isV4Supported = await this.supportsInterface(
        v4Contracts.TradeTrustToken__factory,
        constants.contractInterfaceId.TradeTrustTokenMintable,
        provider
      );
      const isV5Supported = await this.supportsInterface(
        v5Contracts.TradeTrustToken__factory,
        constants$1.contractInterfaceId.TradeTrustTokenMintable,
        provider
      );
      if (!isV4Supported && !isV5Supported)
        throw new Error("Token registry version is not supported.");
    } catch (error) {
      if (error.message === "Token registry version is not supported.") {
        throw error;
      } else {
        throw new Error(
          `Network Error: Unable to verify token registry. Please check the RPC URL or token registry address.`
        );
      }
    }
  }
  // Private helper method to check if a contract supports a specific interface ID.
  async supportsInterface(contractFactory, interfaceId, provider) {
    const contract = contractFactory.connect(this.statusConfig.tokenRegistry, provider);
    return contract.supportsInterface(interfaceId);
  }
}

export { DocumentBuilder };
